import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { api, ApiError, mediaUrl, type ProductView, type UploadedMedia } from '../lib/api'
import { Card } from '../ui'
import { ReadOnlyNotice } from './ReadOnlyNotice'

const formatMoney = (minor: number, currency: string) => `${(minor / 100).toFixed(2)} ${currency}`

/**
 * "Sell in the chat" — a product catalogue an agent attaches to a message, not a native
 * WhatsApp catalog/cart. There is no product-message type reachable through Wami's QR-paired
 * connection (that needs Meta's Cloud API with Commerce Manager provisioned), so sending a
 * product composes an ordinary image+text reply carrying a generated payment link — see the
 * "Send product" action in Inbox.tsx's composer, which is where this catalogue actually gets
 * used day to day. This screen is just where it's stocked.
 */
export function Products() {
  const queryClient = useQueryClient()
  const [creating, setCreating] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)

  const productsQuery = useQuery({ queryKey: ['products'], queryFn: api.products, retry: false })
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['products'] })
  const remove = useMutation({ mutationFn: (id: string) => api.deleteProduct(id), onSuccess: refresh })
  const toggleActive = useMutation({
    mutationFn: (input: { id: string; active: boolean }) => api.updateProduct(input.id, { active: input.active }),
    onSuccess: refresh,
  })

  if (productsQuery.isPending) return <div className="cad-card" style={{ height: '200px' }} />
  if (productsQuery.isError) {
    const message = productsQuery.error instanceof ApiError ? productsQuery.error.message : 'Unavailable.'
    return (
      <div className="cad-card">
        <p className="cad-muted">{message}</p>
      </div>
    )
  }

  const { products, canManage } = productsQuery.data!

  return (
    <>
      {!canManage && <ReadOnlyNotice what="the product catalogue" />}
      <header className="stack gap-1">
        <span className="cad-eyebrow">Catalogue</span>
        <div className="row between wrap gap-3" style={{ alignItems: 'center' }}>
          <h1 className="cad-title">{products.length} product{products.length === 1 ? '' : 's'}</h1>
          {canManage && !creating && !editingId && (
            <button type="button" className="cad-btn" onClick={() => setCreating(true)}>
              New product
            </button>
          )}
        </div>
        <p className="cad-muted" style={{ maxWidth: '64ch' }}>
          Attach any of these to a chat and Wami generates a payment link on the spot — see
          "Send product" in the Inbox composer. Sent as a normal WhatsApp image and text message
          with the link included, since there is no native WhatsApp catalog card this connection
          can send.
        </p>
      </header>

      {creating && (
        <ProductEditor
          onDone={() => {
            setCreating(false)
            void refresh()
          }}
          onCancel={() => setCreating(false)}
        />
      )}

      <section className="cad-card">
        {(remove.error || toggleActive.error) && (
          <p className="cad-note" style={{ borderColor: 'var(--cad-bad)', marginBottom: '12px' }}>
            {(() => {
              const err = remove.error ?? toggleActive.error
              return err instanceof ApiError || err instanceof Error ? err.message : 'That did not go through.'
            })()}
          </p>
        )}
        {products.length === 0 ? (
          <p className="cad-muted">
            No products yet. A product can be dropped into a chat as a card with a price and a
            payment link, rather than retyping the details every time.{' '}
            {canManage ? 'Add your first with “New product” above.' : 'Your role cannot add them.'}
          </p>
        ) : (
          <div className="stack gap-3">
            {products.map((product) =>
              editingId === product.id ? (
                <ProductEditor
                  key={product.id}
                  product={product}
                  onDone={() => {
                    setEditingId(null)
                    void refresh()
                  }}
                  onCancel={() => setEditingId(null)}
                />
              ) : (
                <div key={product.id} className="row between wrap gap-3" style={{ alignItems: 'center' }}>
                  <div className="row gap-3" style={{ alignItems: 'center' }}>
                    {product.mediaKey && (
                      <img
                        src={mediaUrl(product.mediaKey)}
                        alt=""
                        style={{ width: '44px', height: '44px', objectFit: 'cover', borderRadius: 'var(--cad-radius)' }}
                      />
                    )}
                    <div className="stack gap-1">
                      <span style={{ fontWeight: 600 }}>{product.name}</span>
                      <span className="cad-muted" style={{ fontSize: 'var(--cad-text-md)' }}>
                        {formatMoney(product.priceMinor, product.currency)}
                        {!product.active && ' · Inactive'}
                      </span>
                    </div>
                  </div>
                  {canManage && (
                    <div className="row gap-2">
                      <button
                        type="button"
                        className="cad-btn"
                        data-variant="text"
                        onClick={() => toggleActive.mutate({ id: product.id, active: !product.active })}
                      >
                        {product.active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button type="button" className="cad-btn" data-variant="text" onClick={() => setEditingId(product.id)}>
                        Edit
                      </button>
                      <button
                        type="button"
                        className="cad-btn"
                        data-variant="text"
                        onClick={() => {
                          if (confirm(`Delete "${product.name}"? Past payment links keep working; you just can't send it again.`)) {
                            remove.mutate(product.id)
                          }
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              ),
            )}
          </div>
        )}
      </section>
    </>
  )
}

function ProductEditor({
  product,
  onDone,
  onCancel,
}: {
  product?: ProductView
  onDone: () => void
  onCancel: () => void
}) {
  const [name, setName] = useState(product?.name ?? '')
  const [description, setDescription] = useState(product?.description ?? '')
  const [price, setPrice] = useState(product ? (product.priceMinor / 100).toFixed(2) : '')
  const [currency, setCurrency] = useState(product?.currency ?? 'USD')
  const [media, setMedia] = useState<UploadedMedia | null>(
    product?.mediaKey
      ? {
          key: product.mediaKey,
          kind: product.mediaKind ?? 'image',
          mime: product.mediaMime ?? 'image/jpeg',
          fileName: product.mediaFileName ?? 'image',
          size: product.mediaSize ?? 0,
        }
      : null,
  )
  const [mediaPreview, setMediaPreview] = useState<string | null>(product?.mediaKey ? mediaUrl(product.mediaKey) : null)

  const upload = useMutation({
    mutationFn: api.uploadMedia,
    onSuccess: (result) => setMedia(result),
  })

  const priceMinor = Math.round(Number(price) * 100)
  const valid = name.trim() && Number.isFinite(priceMinor) && priceMinor > 0 && currency.trim()

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        description: description.trim() || null,
        priceMinor,
        currency: currency.trim().toUpperCase(),
        media,
      }
      return product ? api.updateProduct(product.id, body) : api.createProduct(body)
    },
    onSuccess: onDone,
  })

  const error = save.error
  const errorMessage = error instanceof ApiError || error instanceof Error ? error.message : undefined

  return (
    <Card variant="outlined">
      <form
        className="stack gap-4"
        onSubmit={(event: FormEvent) => {
          event.preventDefault()
          if (valid) save.mutate()
        }}
      >
        <h2 className="cad-section-title">{product ? 'Edit product' : 'New product'}</h2>

        <div className="field-grid">
          <div className="cad-field">
            <label htmlFor="prd-name">Name</label>
            <input id="prd-name" className="cad-input" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="row gap-2">
            <div className="cad-field" style={{ maxWidth: '140px' }}>
              <label htmlFor="prd-price">Price</label>
              <input
                id="prd-price"
                className="cad-input"
                type="number"
                min="0"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
            </div>
            <div className="cad-field" style={{ maxWidth: '100px' }}>
              <label htmlFor="prd-currency">Currency</label>
              <input
                id="prd-currency"
                className="cad-input"
                value={currency}
                maxLength={3}
                onChange={(e) => setCurrency(e.target.value.toUpperCase())}
              />
            </div>
          </div>
        </div>

        <div className="cad-field">
          <label htmlFor="prd-description">Description</label>
          <input
            id="prd-description"
            className="cad-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What a customer sees under the name and price"
          />
        </div>

        <div className="stack gap-2">
          <span className="cad-label">Image (optional)</span>
          {mediaPreview && (
            <img src={mediaPreview} alt="" style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: 'var(--cad-radius)' }} />
          )}
          <div className="row gap-2" style={{ alignItems: 'center' }}>
            <label htmlFor="prd-media" className="cad-btn" data-variant="quiet" role="button">
              {media ? 'Replace image' : 'Add image'}
            </label>
            <input
              id="prd-media"
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (!file) return
                setMediaPreview(URL.createObjectURL(file))
                upload.mutate(file)
              }}
            />
            {media && (
              <button
                type="button"
                className="cad-btn"
                data-variant="text"
                onClick={() => {
                  setMedia(null)
                  setMediaPreview(null)
                }}
              >
                Remove
              </button>
            )}
            {upload.isPending && <span className="cad-muted">Uploading…</span>}
          </div>
        </div>

        {errorMessage && <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>{errorMessage}</p>}

        <div className="row gap-2 wrap">
          <button type="submit" className="cad-btn" disabled={!valid || save.isPending || upload.isPending}>
            {save.isPending ? 'Saving…' : 'Save product'}
          </button>
          <button type="button" className="cad-btn" data-variant="quiet" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </Card>
  )
}
